import React, { useEffect, useMemo } from "react";
import { CustomIcon } from "./Icons";
import Button from "./Button";
import { Quest, QuestState } from "../types/quests/indext";
import { Link } from "react-router-dom";
import useQuests from "../hooks/useQuests";
import CircularProgress from "./CircularProgress";
import Countdown, { CountdownRenderProps } from "react-countdown";
import { useAppTranslation } from "../i18n/useAppTranslation";
import {
  compactNumber,
  isListedQuest,
  orderQuests,
  parseQuestRewards,
  questProgress,
} from "../utils/quest-list";
import "./QuestList.css";

type Props = {
  // Called when a quest's "Go" link leaves the list, so its container can close.
  onNavigate: () => void;
};

type ListItem = {
  id: number;
  quest: Quest;
  state: QuestState;
  progress?: number;
  startDate?: string;
};

const pad = (value: number) => String(value).padStart(2, "0");
const renderTimeLeft = ({ days, hours, minutes, seconds, completed }: CountdownRenderProps) =>
  completed ? null : (
    <span className="ql-time" aria-hidden="true">
      <span className="ql-time-icon">⏱</span>
      {days > 0 ? `${days}d ${pad(hours)}h` : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`}
    </span>
  );

// The player's quest list with claiming: the Quests tab of the Events dialog.
// Quests ready to claim come first, then the rest, closest to done first. It
// fetches the list each time it is shown.
const QuestList = (props: Props) => {
  const { t } = useAppTranslation(["common"]);
  const { actionLoading, handleClaim, loading: fetching, quests, getQuests } =
    useQuests();
  // The stored list (the map refreshes it on every visit) shows at once and is
  // replaced when the fetch returns; the spinner is only for an empty store.
  const listed = useMemo(
    () => orderQuests((quests as unknown as ListItem[]).filter(isListedQuest)),
    [quests]
  );
  const loading = fetching && listed.length === 0;
  const ready = listed.filter((item) => item.state === QuestState.Done);
  const others = listed.filter((item) => item.state !== QuestState.Done);

  useEffect(() => {
    getQuests();
  }, []);

  const goButton = (quest: Quest) => {
    if (!quest.link) return null;
    const button = (
      <Button onClick={props.onNavigate} className="ql-button" size="small" variant="primary">
        {t("common:quests.go")}
      </Button>
    );
    return quest.link.includes("http") ? (
      <a href={quest.link}>{button}</a>
    ) : (
      <Link to={quest.link}>{button}</Link>
    );
  };

  const renderQuest = ({ id, quest, state, progress, startDate }: ListItem) => {
    const rewards = parseQuestRewards(quest.rewards);
    const { current, target, ratio } = questProgress({ state, progress, quest });
    const isReady = state === QuestState.Done;
    const endsAt =
      state === QuestState.Open && quest.durtaion && startDate
        ? new Date(new Date(startDate).getTime() + quest.durtaion * 24 * 3600 * 1000)
        : null;
    return (
      <li
        key={id}
        className={`ql-quest ql-quest--${
          isReady ? "ready" : state === QuestState.Open ? "open" : state === QuestState.Claimed ? "claimed" : "expired"
        }`}
      >
        <div className="ql-icon">
          {quest.icon ? <img src={quest.icon} alt="" /> : <span aria-hidden="true">✦</span>}
        </div>
        <div className="ql-body">
          <div className="ql-title">{quest.title}</div>
          {quest.description && (
            <div className="ql-desc" title={quest.description}>
              {quest.description}
            </div>
          )}
          {target > 1 && state !== QuestState.Expired && (
            <div className="ql-progress">
              <div
                className="ql-bar"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={target}
                aria-valuenow={current}
                aria-label={quest.title}
              >
                <span style={{ width: `${Math.round(ratio * 100)}%` }} />
              </div>
              <span className="ql-count">
                {compactNumber(current)} / {compactNumber(target)}
              </span>
            </div>
          )}
        </div>
        {rewards.length > 0 && (
          <ul className="ql-rewards">
            {rewards.map((reward, index) => (
              <li
                key={index}
                className="ql-reward"
                title={`${reward.name ?? ""} ×${(Number(reward.quantity) || 0).toLocaleString()}`}
              >
                {reward.icon ? <img src={reward.icon} alt={reward.name ?? ""} /> : <span>{reward.name}</span>}
                {Boolean(reward.level) && <span className="ql-reward-level">{reward.level}</span>}
                <span className="ql-reward-qty">×{compactNumber(Number(reward.quantity) || 0)}</span>
              </li>
            ))}
          </ul>
        )}
        <div className="ql-action">
          {isReady && (
            <Button
              loading={actionLoading === id}
              disabled={Boolean(actionLoading)}
              onClick={() => handleClaim(id)}
              className="ql-button ql-claim"
              size="small"
              variant="success"
            >
              {t("common:quests.claim")}
            </Button>
          )}
          {state === QuestState.Open && goButton(quest)}
          {state === QuestState.Open && endsAt && <Countdown date={endsAt} renderer={renderTimeLeft} />}
          {state === QuestState.Claimed && (
            <span className="ql-chip">
              <CustomIcon icon="tick" width={14} height={16} />
              {t("common:quests.claimed")}
            </span>
          )}
          {state === QuestState.Expired && (
            <span className="ql-chip">{t("common:quests.expired")}</span>
          )}
        </div>
      </li>
    );
  };

  return (
    <div className="ql">
      {loading && (
        <div className="ql-placeholder">
          <CircularProgress style={{ margin: 0 }} />
        </div>
      )}
      {!loading && listed.length === 0 && (
        <div className="ql-placeholder text-secondary">{t("common:quests.empty")}</div>
      )}
      {ready.length > 0 && (
        <section aria-label={t("common:quests.readyToClaim")}>
          <h3 className="ql-group ql-group--ready">
            {t("common:quests.readyToClaim")}
            <span className="ql-group-count">{ready.length}</span>
          </h3>
          <ul className="ql-items">{ready.map(renderQuest)}</ul>
        </section>
      )}
      {others.length > 0 && (
        <section aria-label={t("common:quests.inProgress")}>
          <h3 className="ql-group">
            {t("common:quests.inProgress")}
            <span className="ql-group-count">{others.length}</span>
          </h3>
          <ul className="ql-items">{others.map(renderQuest)}</ul>
        </section>
      )}
    </div>
  );
};

export default QuestList;
